import React, { useState, useEffect } from 'react';
import {
  Users,
  DollarSign,
  UserCheck,
  Shield,
  TrendingUp,
  Activity,
  CreditCard,
  Building2,
  MessageSquare,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import UserManagement from './admin/UserManagement';
import PaymentManagement from './admin/PaymentManagement';
import BusinessManagement from './admin/BusinessManagement';
import SystemLogs from './admin/SystemLogs';
import BotManagement from './admin/BotManagement';
import StuckReceiptsMonitor from './admin/StuckReceiptsMonitor';
import ProductOrdersManagement from './admin/ProductOrdersManagement';

interface DashboardStats {
  totalUsers: number;
  activeSubscriptions: number;
  totalRevenue: number;
  receiptsProcessed: number;
  pendingPayments: number;
  totalBusinesses: number;
}

export default function SuperAdminDashboard() {
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'payments' | 'businesses' | 'logs' | 'bot' | 'monitor' | 'products'>('overview');
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeSubscriptions: 0,
    totalRevenue: 0,
    receiptsProcessed: 0,
    pendingPayments: 0,
    totalBusinesses: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [usersResult, receiptsResult, paymentsResult, businessesResult] = await Promise.all([
        supabase.from('users').select('*', { count: 'exact' }),
        supabase.from('receipts').select('*', { count: 'exact' }),
        supabase.from('payment_transactions').select('amount, status'),
        supabase.from('businesses').select('*', { count: 'exact' }),
      ]);

      const activeUsers = usersResult.data?.filter((u: any) =>
        u.access_level && u.access_level.toUpperCase() !== 'FREE'
      ).length || 0;

      const totalRevenue = paymentsResult.data
        ?.filter((p: any) => p.status === 'completed')
        .reduce((sum: number, p: any) => sum + parseFloat(p.amount || 0), 0) || 0;

      const pendingPayments = paymentsResult.data?.filter((p: any) => p.status === 'pending').length || 0;

      setStats({
        totalUsers: usersResult.count || 0,
        activeSubscriptions: activeUsers,
        totalRevenue,
        receiptsProcessed: receiptsResult.count || 0,
        pendingPayments,
        totalBusinesses: businessesResult.count || 0,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color }: any) => (
    <div className="card">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">{title}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 ${color.replace('text', 'bg')}/10 rounded-lg flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-primary rounded-lg flex items-center justify-center">
                <span className="text-xl font-bold text-white">L</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">LytSpeed Receipts</h1>
                <p className="text-sm text-gray-600 dark:text-gray-100">Super Admin Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-brand-primary" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-100">Super Admin</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {import.meta.env.VITE_DEVELOPMENT_MODE === 'true' && (
          <div className="mb-6 bg-gradient-to-r from-orange-100 to-red-100 border-2 border-orange-300 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-900">Preview Mode: Super Admin</p>
                <p className="text-sm text-gray-700">
                  Full system access - manage users, businesses, payments, and system settings.
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="flex gap-4 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: TrendingUp },
            { id: 'monitor', label: 'Processing Monitor', icon: Activity },
            { id: 'products', label: 'Product Orders', icon: CreditCard },
            { id: 'users', label: 'User Management', icon: Users },
            { id: 'payments', label: 'Payments', icon: DollarSign },
            { id: 'businesses', label: 'Businesses', icon: Building2 },
            { id: 'bot', label: 'Bot Management', icon: MessageSquare },
            { id: 'logs', label: 'System Logs', icon: Activity },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-brand-primary text-white shadow-brand-lg'
                  : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <StatCard
                icon={Users}
                title="Total Users"
                value={loading ? '...' : stats.totalUsers.toLocaleString()}
                subtitle="Registered Telegram users"
                color="text-blue-600"
              />
              <StatCard
                icon={UserCheck}
                title="Active Subscriptions"
                value={loading ? '...' : stats.activeSubscriptions.toLocaleString()}
                subtitle="Premium & Enterprise users"
                color="text-green-600"
              />
              <StatCard
                icon={DollarSign}
                title="Total Revenue"
                value={loading ? '...' : `$${stats.totalRevenue.toFixed(2)}`}
                subtitle="All-time completed payments"
                color="text-emerald-600"
              />
              <StatCard
                icon={Activity}
                title="Receipts Processed"
                value={loading ? '...' : stats.receiptsProcessed.toLocaleString()}
                subtitle="Total receipts in system"
                color="text-purple-600"
              />
              <StatCard
                icon={CreditCard}
                title="Pending Payments"
                value={loading ? '...' : stats.pendingPayments.toLocaleString()}
                subtitle="Awaiting confirmation"
                color="text-orange-600"
              />
              <StatCard
                icon={Building2}
                title="Businesses"
                value={loading ? '...' : stats.totalBusinesses.toLocaleString()}
                subtitle="Active business accounts"
                color="text-indigo-600"
              />
            </div>

            <div className="card-gradient">
              <h3 className="text-lg font-semibold mb-2">Quick Actions</h3>
              <p className="text-white/80 text-sm mb-4">Common administrative tasks</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  onClick={() => setActiveTab('users')}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-lg text-left transition-all backdrop-blur-sm border border-white/20"
                >
                  <Users className="w-5 h-5 mb-1" />
                  <p className="font-medium">Manage Users</p>
                  <p className="text-xs text-white/70">View and edit user access</p>
                </button>
                <button
                  onClick={() => setActiveTab('payments')}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-lg text-left transition-all backdrop-blur-sm border border-white/20"
                >
                  <CreditCard className="w-5 h-5 mb-1" />
                  <p className="font-medium">Process Payments</p>
                  <p className="text-xs text-white/70">Review payment status</p>
                </button>
                <button
                  onClick={() => setActiveTab('businesses')}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-lg text-left transition-all backdrop-blur-sm border border-white/20"
                >
                  <Building2 className="w-5 h-5 mb-1" />
                  <p className="font-medium">Manage Businesses</p>
                  <p className="text-xs text-white/70">Assign team leaders</p>
                </button>
                <button
                  onClick={() => setActiveTab('logs')}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-lg text-left transition-all backdrop-blur-sm border border-white/20"
                >
                  <Activity className="w-5 h-5 mb-1" />
                  <p className="font-medium">View System Logs</p>
                  <p className="text-xs text-white/70">Monitor system activity</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'monitor' && <StuckReceiptsMonitor />}
        {activeTab === 'products' && <ProductOrdersManagement />}
        {activeTab === 'users' && <UserManagement />}
        {activeTab === 'payments' && <PaymentManagement />}
        {activeTab === 'businesses' && <BusinessManagement />}
        {activeTab === 'bot' && <BotManagement />}
        {activeTab === 'logs' && <SystemLogs />}
      </div>
    </div>
  );
}
